/**
 * One incident: its evidence, its channel, and its response.
 *
 * The channel is not a side panel. Every automated action posts there, so it *is* the record of
 * what happened, and it sits alongside the evidence rather than behind a tab — an analyst should
 * not have to go looking to find out that containment already ran and failed.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  type ChannelMessage,
  type IncidentDetail as Detail,
  type PlaybookRun,
  type RollbackPlan,
} from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Modal,
  Mono,
  Note,
  ObservableList,
  SeverityChip,
  StatusChip,
  fmtRelative,
  fmtTime,
  useAsync,
  usePoll,
  useToast,
} from '../components/ui'

const NEXT_STATUS: Record<string, { value: string; label: string; danger?: boolean }[]> = {
  new: [
    { value: 'triage', label: 'Take for triage' },
    { value: 'contained', label: 'Mark contained' },
    { value: 'false_positive', label: 'False positive', danger: true },
  ],
  triage: [
    { value: 'contained', label: 'Mark contained' },
    { value: 'closed', label: 'Close' },
    { value: 'false_positive', label: 'False positive', danger: true },
  ],
  contained: [
    { value: 'eradicated', label: 'Mark eradicated' },
    { value: 'false_positive', label: 'False positive', danger: true },
  ],
  eradicated: [{ value: 'closed', label: 'Close' }],
  closed: [],
  false_positive: [],
}

export default function IncidentDetail() {
  const { id = '' } = useParams()
  const { can } = useSession()
  const toast = useToast()
  const detail = useAsync<Detail>(() => api.get<Detail>(`/incidents/${id}`), [id])
  usePoll(detail.reload, 12000)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState('')

  function setStatus(status: string) {
    setBusy(true)
    api
      .post(`/incidents/${id}/status`, { status })
      .then(() => {
        toast('ok', `Status set to ${status.replace(/_/g, ' ')}`)
        detail.reload()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  function post(channelId: string) {
    const body = draft.trim()
    if (!body) return
    setDraft('')
    api
      .post(`/channels/${channelId}/messages`, { body })
      .then(() => detail.reload())
      .catch((e: Error) => {
        toast('error', e.message)
        setDraft(body)
      })
  }

  function advance(runId: string) {
    setBusy(true)
    api
      .post(`/runs/${runId}/advance`)
      .then(() => detail.reload())
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Async state={detail}>
      {(d) => {
        const i = d.incident
        const transitions = NEXT_STATUS[i.status] ?? []
        const failedActions = d.messages.filter((m) => m.kind === 'action' && m.success === false)

        return (
          <>
            <div className="page-head">
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                  <Link to="/incidents" className="small">
                    ← Incidents
                  </Link>
                  <Mono copy>{i.id}</Mono>
                </div>
                <h1>{i.title}</h1>
                <div className="sub">{i.rationale}</div>
              </div>
              <div className="actions">
                <SeverityChip severity={i.severity} score={i.score} />
                <StatusChip status={i.status} />
              </div>
            </div>

            {failedActions.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <Note tone="danger">
                  <strong>{failedActions.length} action(s) failed.</strong> The response did not
                  complete, so anything it was meant to contain may still be live:{' '}
                  {failedActions.map((m) => m.action_id).join(', ')}.
                </Note>
              </div>
            )}

            {can('write_incidents') && transitions.length > 0 && (
              <div className="row wrap" style={{ marginBottom: 14 }}>
                <span className="small muted">Move to:</span>
                {transitions.map((t) => (
                  <button
                    key={t.value}
                    className={t.danger ? 'danger sm' : 'sm'}
                    disabled={busy || (t.value === 'closed' && !can('close_incident'))}
                    title={
                      t.value === 'closed' && !can('close_incident')
                        ? 'Closing an incident is a separate capability from working it'
                        : undefined
                    }
                    onClick={() => setStatus(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            <div className="grid cols-2">
              <div className="stack">
                <Card title="What the analyst assistant makes of it">
                  {d.assessment.from_model ? (
                    <Chip tone="outline" title={`Produced by ${d.assessment.model}`}>
                      AI analyst · {d.assessment.model}
                    </Chip>
                  ) : (
                    <Chip tone="neutral">deterministic summary — no model configured</Chip>
                  )}
                  <div className="text" style={{ whiteSpace: 'pre-wrap', marginTop: 8, fontSize: 13 }}>
                    {d.assessment.text}
                  </div>
                </Card>

                <Card title={`Contributing signals (${d.signals.length})`} tight>
                  {d.signals.length === 0 ? (
                    <Empty title="No signals retained">
                      The signals that raised this incident have aged out of the in-memory window.
                    </Empty>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Severity</th>
                            <th>Signal</th>
                            <th>Technique</th>
                            <th>Evidence</th>
                            <th>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.signals.map((s) => (
                            <tr key={s.id}>
                              <td><SeverityChip severity={s.severity} /></td>
                              <td>
                                <div style={{ fontWeight: 550 }}>{s.title}</div>
                                <div className="faint small">{s.rationale}</div>
                                <div style={{ marginTop: 4 }}>
                                  <ObservableList observables={s.observables} max={5} />
                                </div>
                              </td>
                              <td className="small">
                                {s.techniques.map((t) => (
                                  <div key={t.id}>
                                    <Chip tone="outline" title={t.name}>
                                      {t.id}
                                    </Chip>
                                  </div>
                                ))}
                              </td>
                              <td className="small tnum">
                                {s.evidence.length}
                                {s.evidence_count > s.evidence.length && (
                                  <span className="faint"> of {s.evidence_count}</span>
                                )}
                              </td>
                              <td className="small muted nowrap">{fmtRelative(s.timestamp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                <Card title={`Evidence (${d.events.length} events)`} tight>
                  {d.events.length === 0 ? (
                    <Empty title="No events retained" />
                  ) : (
                    <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Source</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.events.map((e) => (
                            <tr key={e.id}>
                              <td className="small muted nowrap">{fmtTime(e.timestamp)}</td>
                              <td className="small">
                                <Chip tone="outline">{e.class}</Chip>{' '}
                                <span className="faint">{e.source}</span>
                              </td>
                              <td className="small">
                                <div className="truncate" title={e.message} style={{ maxWidth: '52ch' }}>
                                  {e.message}
                                </div>
                                <ObservableList observables={e.observables} max={4} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {d.runs.length > 0 && <RunPanel runs={d.runs} onAdvance={advance} busy={busy} />}
              </div>

              <ChannelPanel
                messages={d.messages}
                channelId={d.channel?.id ?? ''}
                archived={d.channel?.archived ?? false}
                draft={draft}
                setDraft={setDraft}
                onPost={post}
                canPost={can('write_incidents')}
              />
            </div>
          </>
        )
      }}
    </Async>
  )
}

/**
 * Walking a run back.
 *
 * The plan is shown before anything happens, and it shows what it *cannot* undo just as
 * prominently as what it can. A rollback that quietly walked back four of six containments and
 * reported success would leave an analyst believing the estate is where they left it.
 */
function Rollback({ run }: { run: PlaybookRun }) {
  const { can } = useSession()
  const toast = useToast()
  const [plan, setPlan] = useState<RollbackPlan | null>(null)
  const [busy, setBusy] = useState(false)

  function load() {
    setBusy(true)
    api
      .get<RollbackPlan>(`/runs/${run.id}/rollback`)
      .then(setPlan)
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  function perform() {
    setBusy(true)
    api
      .post<{ summary: string }>(`/runs/${run.id}/rollback`, {})
      .then((r) => {
        toast('ok', r.summary)
        setPlan(null)
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  const standing = (plan?.skipped ?? []).filter((s) => s.still_applied)

  return (
    <>
      <button className="sm" disabled={busy} onClick={load}>
        Roll back…
      </button>
      {plan && (
        <Modal
          title={`Roll back ${run.playbook_id}`}
          onClose={() => setPlan(null)}
          footer={
            <>
              <button onClick={() => setPlan(null)}>Cancel</button>
              {can('run_playbook') && plan.steps.length > 0 && (
                <button className="primary" disabled={busy} onClick={perform}>
                  {busy ? 'Starting…' : `Undo ${plan.steps.length} step(s)`}
                </button>
              )}
            </>
          }
        >
          {plan.steps.length === 0 ? (
            <Note>Nothing in this run can be walked back automatically.</Note>
          ) : (
            <>
              <p className="small muted">
                Newest first — the last thing done is the first thing undone. This starts an
                ordinary playbook run, so its own approval gates still apply: undoing an
                estate-wide change needs two signatures, exactly as making it did.
              </p>
              <ul style={{ paddingLeft: 18 }}>
                {plan.steps.map((s) => (
                  <li key={s.from_node} style={{ marginBottom: 6 }}>
                    <Mono>{s.action_id}</Mono> — undoes {s.undoes}{' '}
                    {s.uncertain && <Chip tone="warn">outcome never confirmed</Chip>}
                    <div className="faint small">{s.why}</div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {standing.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Note tone="warn">
                <strong>{standing.length} change(s) to the estate cannot be undone here</strong> and
                need doing by hand:
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {standing.map((s) => (
                    <li key={s.node_id}>
                      <Mono>{s.action_id}</Mono> — {s.reason}
                    </li>
                  ))}
                </ul>
              </Note>
            </div>
          )}
        </Modal>
      )}
    </>
  )
}

function RunPanel({
  runs,
  onAdvance,
  busy,
}: {
  runs: PlaybookRun[]
  onAdvance: (id: string) => void
  busy: boolean
}) {
  const { can } = useSession()
  return (
    <>
      {runs.map((r) => (
        <Card
          key={r.id}
          title={`Playbook run — ${r.playbook_id}`}
          subtitle={
            <>
              <Mono>{r.id}</Mono> · pinned to playbook v{r.playbook_version} · {r.steps.length} step(s)
            </>
          }
          actions={
            <>
              <Chip
                tone={
                  r.state === 'halted'
                    ? 'danger'
                    : r.state === 'completed'
                      ? 'ok'
                      : r.state === 'awaiting_approval'
                        ? 'warn'
                        : 'neutral'
                }
              >
                {r.state.replace(/_/g, ' ')}
              </Chip>
              {can('run_playbook') && r.state !== 'completed' && r.state !== 'halted' && r.state !== 'awaiting_approval' && (
                <button className="sm" disabled={busy} onClick={() => onAdvance(r.id)}>
                  Advance
                </button>
              )}
              {(r.state === 'completed' || r.state === 'halted') && <Rollback run={r} />}
            </>
          }
        >
          {r.state === 'awaiting_approval' && (
            <div style={{ marginBottom: 10 }}>
              <Note tone="warn">
                Paused for an approval. It will not proceed on its own, and it will not
                auto-approve if nobody answers. <Link to="/approvals">Go to approvals</Link>.
              </Note>
            </div>
          )}
          {r.state === 'halted' && (
            <div style={{ marginBottom: 10 }}>
              <Note tone="danger">Halted: {r.outcome}. A human needs to take it from here.</Note>
            </div>
          )}
          {(r.simulated_steps ?? []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <Note tone="warn">
                {(r.simulated_steps ?? []).length} step(s) in this run were{' '}
                <strong>simulated</strong>: no integration is configured for them, so nothing on the
                estate was changed. Treat this incident as uncontained.
              </Note>
            </div>
          )}
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {r.steps.map((s, idx) => (
              <li key={`${s.node_id}-${idx}`} style={{ marginBottom: 3 }}>
                <span
                  aria-hidden
                  style={{
                    color:
                      s.outcome === 'failed' || s.outcome === 'denied' || s.outcome === 'timed_out'
                        ? 'var(--danger)'
                        : s.outcome === 'succeeded' || s.outcome === 'approved'
                          ? 'var(--ok)'
                          : 'var(--text-faint)',
                  }}
                >
                  {s.outcome === 'failed' || s.outcome === 'denied' || s.outcome === 'timed_out'
                    ? '✗'
                    : s.outcome === 'succeeded' || s.outcome === 'approved'
                      ? '✓'
                      : '▸'}
                </span>{' '}
                <strong>{s.node_label}</strong> <span className="muted">{s.detail}</span>
              </li>
            ))}
          </ol>
        </Card>
      ))}
    </>
  )
}

function ChannelPanel({
  messages,
  channelId,
  archived,
  draft,
  setDraft,
  onPost,
  canPost,
}: {
  messages: ChannelMessage[]
  channelId: string
  archived: boolean
  draft: string
  setDraft: (s: string) => void
  onPost: (channelId: string) => void
  canPost: boolean
}) {
  const [filter, setFilter] = useState<'all' | 'timeline' | 'actions'>('all')
  const shown = messages.filter((m) => {
    if (filter === 'all') return true
    if (filter === 'actions') return m.kind === 'action'
    return m.kind !== 'chat'
  })

  return (
    <Card
      title="Incident channel"
      subtitle="Every action taken on this incident is posted here, automated or not"
      actions={
        <select value={filter} onChange={(e) => setFilter(e.target.value as never)} style={{ width: 'auto' }} aria-label="Filter">
          <option value="all">Everything</option>
          <option value="timeline">Timeline only</option>
          <option value="actions">Actions only</option>
        </select>
      }
    >
      <div style={{ maxHeight: 560, overflowY: 'auto', paddingRight: 4 }}>
        {shown.length === 0 && <Empty title="Nothing posted yet" />}
        {shown.map((m) => {
          const isAi = m.author.kind === 'ai_analyst'
          const isSystem = m.author.kind === 'system'
          const who = isSystem ? 'Bastion' : (m.author as { display_name: string }).display_name
          const advisory = m.kind === 'assessment' && (isAi || isSystem)
          return (
            <div key={m.id} className={`msg${advisory ? ' advisory' : ''}`}>
              <div className={`avatar${isAi ? ' ai' : isSystem ? ' system' : ''}`} aria-hidden>
                {isSystem ? '⌬' : isAi ? 'AI' : who.slice(0, 2).toUpperCase()}
              </div>
              <div className="body">
                <div className="meta">
                  <span className="who">{who}</span>
                  {isAi && (
                    <Chip tone="outline" title={`Model: ${(m.author as { model: string }).model}`}>
                      AI analyst
                    </Chip>
                  )}
                  {m.kind !== 'chat' && <Chip tone="outline">{m.kind.replace(/_/g, ' ')}</Chip>}
                  {m.kind === 'action' && (
                    <Chip tone={m.success === false ? 'danger' : 'ok'}>
                      {m.action_id} {m.success === false ? 'failed' : 'ok'}
                    </Chip>
                  )}
                  <span className="faint">{fmtTime(m.at)}</span>
                </div>
                <div className="text">{m.body}</div>
              </div>
            </div>
          )
        })}
      </div>

      {archived ? (
        <Note>This channel is archived because the incident is closed. The record is kept.</Note>
      ) : canPost ? (
        <div className="row" style={{ marginTop: 10, alignItems: 'flex-end' }}>
          <textarea
            rows={2}
            placeholder="Add to the channel…  (⌘/Ctrl+Enter to send)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') onPost(channelId)
            }}
          />
          <button className="primary" onClick={() => onPost(channelId)} disabled={!draft.trim()}>
            Post
          </button>
        </div>
      ) : (
        <Note>Your role is read-only in this workspace.</Note>
      )}
    </Card>
  )
}
